import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { useEffect, useState } from 'react';

import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
} from 'react-dates';
import moment from 'moment';

import {
  DateType,
  DEFAULT_DATE_FORMAT,
  formatISOToDate,
  formatMomentDate,
  getCurrentDate,
  isInclusivelyAfterDay,
} from '@utils/dateUtil';
import { ChevronLeft, ChevronRight } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { useDevice } from '@contexts/device';
import { CalendarHolidaysViewModel } from '@api/v3';

import {
  RadioButton,
  CheckboxContainer,
  DatePickerWrapper,
  NavNextButton,
  NavPrevButton,
  HolidayCard,
  Title,
  Year,
  Icon,
  Holiday,
  WeekHoliday,
  WeekHolidayCard,
  MobileHoliday,
  HolidayCardMobile,
  WeekHolidayMobile,
  Description,
  CheckboxContainerMobile,
  DateWrapper,
  DateContainer,
  YearWrapper,
  YearContainer,
  HolidayContainer,
  Wrapper,
  Container,
  Dates,
  Div,
  Circle,
  HolidayName,
  DayPicker,
  RadioText,
  Line,
  LineContainer,
  MonthText,
  MonthWrapper,
} from '../DatePickerStyle';
import { renderCalendarEvent } from '../helpers';
import { useCalendarHoliday } from './getCalendarHolidays';

type RequiredProps = 'focusedInput' | 'onFocusChange' | 'initialVisibleMonth';

export type DayPickerRangeControllerProps = Omit<
  DayPickerRangeControllerShape,
  RequiredProps
> &
  Partial<Pick<DayPickerRangeControllerShape, RequiredProps>> & {
    isHoliday?: boolean;
    initialData?: CalendarHolidaysViewModel;
    isPadding?: boolean;
  };

const DateRangePicker = ({
  initialVisibleMonth = null,
  onDatesChange,
  startDate,
  onFocusChange,
  isHoliday,
  isPadding,
  ...props
}: DayPickerRangeControllerProps) => {
  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >('startDate');
  const [isNextFocusNull, setIsNextFocusNull] = useState(false);
  const { data: initialData } = useCalendarHoliday();
  const getHolidayText = [
    { name: 'Takvim', value: '1', key: 1 },
    { name: 'Resmi Tatiller', value: '2', key: 2 },
    { name: 'Hafta Sonu Tatilleri', value: '3', key: 3 },
  ];
  useEffect(() => {
    if (focusedInput === 'endDate' && isNextFocusNull) {
      onDatesChange({ startDate, endDate: null });
    }
  }, [focusedInput, isNextFocusNull]);

  const [selectedItems, setSelectedItems] = useState(1);

  const { isDesktop } = useDevice();
  const getDateInUTCFormat = (
    startDate: string,
    endDate: string,
    holidayName?: string
  ) => {
    const momentStartDate = moment.utc(startDate);
    const momentEndDate = moment.utc(endDate);
    const startDay = momentStartDate.format('DD');
    const startMonth = momentStartDate.format('MMMM');
    const endMonth = momentEndDate.format('MMMM');
    const endDay = momentEndDate.format('DD');

    return (
      <>
        {holidayName ? (
          <span>
            {holidayName
              .split(/(\d+)/)
              .map((part, index) =>
                /\d+/.test(part) ? <strong key={index}>{part}</strong> : part
              )}
          </span>
        ) : (
          <>
            {' '}
            {startMonth === endMonth ? (
              <span>
                <b>{startDay}</b> - <b>{endDay}</b> {endMonth}
              </span>
            ) : (
              <span>
                <b>{startDay}</b> {startMonth} - <b>{endDay}</b> {endMonth}
              </span>
            )}
          </>
        )}
      </>
    );
  };
  const dateSelected = (redirectedStartDate, redirectedEndDate) => {
    onDatesChange({
      startDate: moment(
        formatMomentDate(
          redirectedStartDate ? moment(redirectedStartDate) : null,
          DEFAULT_DATE_FORMAT
        ),
        DEFAULT_DATE_FORMAT
      ),
      endDate: moment(
        formatMomentDate(
          redirectedEndDate ? moment(redirectedEndDate) : null,
          DEFAULT_DATE_FORMAT
        ),
        DEFAULT_DATE_FORMAT
      ),
    });
  };
  const groupWeeksByMonth = (weekHolidayData: any[]) => {
    const groupedWeeks: Record<string, any[]> = {};

    weekHolidayData.forEach((item) => {
      const startDate = moment.utc(item.holidayStartDate);
      const endDate = moment.utc(item.holidayEndDate);

      const monthYear = startDate.format('MMMM YYYY'); // Ay ve yıl bilgisini alıyoruz

      if (!groupedWeeks[monthYear]) {
        groupedWeeks[monthYear] = [];
      }

      // Eğer başlangıç ve bitiş tarihleri aynı ay içinde ise, aynı grupta sakla
      if (startDate.month() === endDate.month()) {
        groupedWeeks[monthYear].push(item);
      } else {
        // Farklı aylar için ayrı ayrı gruplama
        groupedWeeks[monthYear].push(item);
      }
    });

    return groupedWeeks;
  };
  const groupedWeeks = groupWeeksByMonth(initialData?.weekendHoliday || []);
  useEffect(() => {
    if (focusedInput === 'endDate' && isNextFocusNull) {
      onDatesChange({ startDate, endDate: null });
    }
  }, [focusedInput, isNextFocusNull]);
  const [month, setMonth] = useState(moment().month() + 1);
  const [year, setYear] = useState(moment().year());
  const [monthMobile, setMonthMobile] = useState(moment().month() + 1);
  const [yearMobile, setYearMobile] = useState(moment().year());
  const [holidayData, setHolidayData] = useState<Array<any>>([]);
  const [holidayDataMobile, setHolidayDataMobile] = useState<Array<any>>([]);
  useEffect(() => {
    setHolidayData(
      (initialData?.calendarHoliday || []).filter(
        (i) =>
          (moment(i.holidayStartDate).month() + 1 === month ||
            moment(i.holidayEndDate).month() + 1 === month) &&
          moment(i.holidayStartDate).year() === year
      )
    );
    setHolidayDataMobile(
      (initialData?.calendarHoliday || []).filter(
        (i) =>
          (moment(i.holidayStartDate).month() + 1 === monthMobile ||
            moment(i.holidayEndDate).month() + 1 === monthMobile) &&
          moment(i.holidayStartDate).year() === yearMobile
      )
    );
  }, [month, monthMobile]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (visibleEntry) {
          const month = visibleEntry.target.getAttribute('data-month');
          const year = visibleEntry.target.getAttribute('data-year');
          setMonthMobile(Number(month));
          setYearMobile(Number(year));
        }
      },
      { threshold: 0.6 } // %60 görünürlüğe ulaştığında tetiklensin
    );

    const monthElements = document.querySelectorAll('.calendar-month');
    monthElements.forEach((el) => observer.observe(el));

    return () => {
      monthElements.forEach((el) => observer.unobserve(el));
    };
  }, []);
  useEffect(() => {
    setHolidayData([]), setHolidayDataMobile([]);
  }, [selectedItems]);

  return (
    <DatePickerWrapper isPadding={isPadding}>
      {isHoliday ?? (
        <Wrapper>
          {getHolidayText?.map((i) => (
            <div key={`holiday-option-${i.key}`}>
              {isDesktop ? (
                <CheckboxContainer
                  onClick={() => setSelectedItems(i.key)}
                  checkColor={selectedItems == i.key}
                >
                  <RadioButton
                    type='radio'
                    name='discount'
                    onChange={() => setSelectedItems(i.key)}
                    checked={selectedItems == i.key}
                  />
                  <RadioText selected={selectedItems == i.key}>
                    {i.name}
                  </RadioText>
                </CheckboxContainer>
              ) : (
                <CheckboxContainerMobile
                  onClick={() => setSelectedItems(i.key)}
                  checkColor={selectedItems == i.key}
                >
                  <RadioButton
                    type='radio'
                    name='discount'
                    onChange={() => setSelectedItems(i.key)}
                    checked={selectedItems == i.key}
                  />

                  <RadioText selected={selectedItems == i.key}>
                    {i.name}
                  </RadioText>
                </CheckboxContainerMobile>
              )}
            </div>
          ))}
        </Wrapper>
      )}

      {selectedItems === 1 && (
        <DayPickerRangeController
          verticalBorderSpacing={1}
          numberOfMonths={2}
          renderNavPrevButton={(props) => (
            <NavPrevButton
              variant='Text'
              {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            >
              <CustomIcon
                icon={ChevronLeft}
                fontSize={12}
                paletteColor='darkGray'
              />
            </NavPrevButton>
          )}
          renderNavNextButton={(props) => (
            <NavNextButton variant='Text' {...props}>
              <CustomIcon
                icon={ChevronRight}
                fontSize={12}
                paletteColor='darkGray'
              />
            </NavNextButton>
          )}
          initialVisibleMonth={initialVisibleMonth}
          focusedInput={props.focusedInput ?? focusedInput}
          daySize={33}
          monthFormat='MMMM YYYY'
          isOutsideRange={(day: DateType) =>
            !isInclusivelyAfterDay(day, getCurrentDate())
          }
          minimumNights={1}
          orientation='horizontal'
          renderMonthElement={({ month }) => (
            <DayPicker
              className='calendar-month'
              data-month={month.month() + 1}
              data-year={month.year()}
            >
              {month.format('MMMM YYYY')}
            </DayPicker>
          )}
          onFocusChange={
            onFocusChange ||
            ((nextFocusedInput) => {
              if (
                focusedInput === 'startDate' &&
                nextFocusedInput === 'endDate'
              ) {
                setIsNextFocusNull(true);
              }
              setFocusedInput(nextFocusedInput || 'startDate');
            })
          }
          startDate={startDate}
          onDatesChange={onDatesChange}
          onNextMonthClick={(e) => {
            setMonth(moment(e).month() + 1);
            setYear(moment(e).year());
          }}
          onPrevMonthClick={(e) => {
            setMonth(moment(e).month() + 1);
            setYear(moment(e).year());
          }}
          renderDayContents={(day) =>
            renderCalendarEvent(day, holidayData, initialData?.calendarHoliday)
          }
          {...props}
          renderMonthText={undefined}
        />
      )}
      {selectedItems === 2 && (
        <>
          {isDesktop ? (
            <Holiday>
              {initialData?.calendarHoliday?.map((item, index) => (
                <HolidayCard
                  key={item.holidayStartDate || index}
                  onClick={() =>
                    dateSelected(
                      item.redirectedStartDate,
                      item.redirectedEndDate
                    )
                  }
                >
                  <DateWrapper>
                    <DateContainer style={{ alignItems: 'start' }}>
                      <Title>{item.holidayName}</Title>

                      <Dates>{item.numberOfDays} Gün</Dates>
                    </DateContainer>
                  </DateWrapper>
                  {item.selectHolidayDescription.map((item) => (
                    <Description key={item.codename}>{item.name}</Description>
                  ))}
                  <Container>
                    <YearWrapper>
                      <YearContainer>
                        <Year>
                          {formatISOToDate(
                            item.holidayStartDate || '',
                            DEFAULT_DATE_FORMAT
                          )}{' '}
                          -{' '}
                          {formatISOToDate(
                            item.holidayEndDate || '',
                            DEFAULT_DATE_FORMAT
                          )}
                        </Year>
                        <Icon>
                          <CustomIcon icon={ChevronRight} fontSize={10} />
                        </Icon>
                      </YearContainer>
                    </YearWrapper>
                  </Container>
                </HolidayCard>
              ))}
            </Holiday>
          ) : (
            <MobileHoliday>
              {initialData?.calendarHoliday?.map((item, index) => (
                <HolidayCardMobile
                  key={item.holidayStartDate || index}
                  onClick={() =>
                    dateSelected(
                      item.redirectedStartDate,
                      item.redirectedEndDate
                    )
                  }
                >
                  <DateWrapper>
                    <HolidayContainer>
                      <Title>{item.holidayName}</Title>
                      <Dates>{item.numberOfDays} Gün</Dates>
                    </HolidayContainer>
                  </DateWrapper>
                  {item.selectHolidayDescription.map((item) => (
                    <Description key={item.codename}>{item.name}</Description>
                  ))}
                  <YearWrapper>
                    <DateContainer>
                      <Year>
                        {formatISOToDate(
                          item.holidayStartDate || '',
                          DEFAULT_DATE_FORMAT
                        )}{' '}
                        -{' '}
                        {formatISOToDate(
                          item.holidayEndDate || '',
                          DEFAULT_DATE_FORMAT
                        )}
                      </Year>
                      <Icon>
                        <CustomIcon icon={ChevronRight} fontSize={10} />
                      </Icon>
                    </DateContainer>
                  </YearWrapper>
                </HolidayCardMobile>
              ))}
            </MobileHoliday>
          )}
        </>
      )}
      {selectedItems === 3 && (
        <>
          {isDesktop ? (
            <WeekHoliday>
              {Object.entries(groupedWeeks).map(([month, weeks]) => (
                <MonthWrapper key={month}>
                  <MonthText>{month.split(' ')[0]}</MonthText>
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      marginBottom: '14px',
                    }}
                    key={month}
                  >
                    {weeks.map((item, index) => (
                      <WeekHolidayCard
                        onClick={() =>
                          dateSelected(
                            item.redirectedStartDate,
                            item.redirectedEndDate
                          )
                        }
                        key={item.holidayStartDate || index}
                      >
                        {item.holidayStartDate && item.holidayEndDate
                          ? getDateInUTCFormat(
                              item.holidayStartDate || '',
                              item.holidayEndDate || '',
                              item.holidayName
                            )
                          : ''}
                      </WeekHolidayCard>
                    ))}
                  </div>
                </MonthWrapper>
              ))}
            </WeekHoliday>
          ) : (
            <WeekHolidayMobile>
              {Object.entries(groupedWeeks).map(([month, weeks]) => (
                <MonthWrapper key={month}>
                  <MonthText>{month.split(' ')[0]}</MonthText>
                  <div
                    style={{
                      display: 'flex',
                      rowGap: '14px',
                      columnGap: '6px',
                      flexWrap: 'wrap',
                      marginBottom: '14px',
                    }}
                    key={month}
                  >
                    {weeks.map((item) => (
                      <WeekHolidayCard
                        onClick={() =>
                          dateSelected(
                            item.redirectedStartDate,
                            item.redirectedEndDate
                          )
                        }
                        key={item.holidayStartDate || item.holidayEndDate}
                      >
                        {item.holidayStartDate && item.holidayEndDate
                          ? getDateInUTCFormat(
                              item.holidayStartDate || '',
                              item.holidayEndDate || '',
                              item.holidayName
                            )
                          : ''}
                      </WeekHolidayCard>
                    ))}
                  </div>
                </MonthWrapper>
              ))}
            </WeekHolidayMobile>
          )}
        </>
      )}
      {selectedItems === 1 ? (
        <>
          {isDesktop ? (
            <>
              {isHoliday ?? (
                <>
                  {holidayData.length > 0 && (
                    <>
                      <LineContainer>
                        <Line />
                      </LineContainer>
                      <Div holidayData={holidayData.length > 0}>
                        {holidayData?.map((item, index) => (
                          <HolidayName key={index}>
                            <Circle />
                            {item.holidayName}
                          </HolidayName>
                        ))}
                      </Div>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {isHoliday ?? (
                <>
                  {holidayDataMobile.length > 0 && (
                    <Div holidayData={holidayDataMobile.length > 0}>
                      {holidayDataMobile?.map((item, index) => (
                        <HolidayName key={index}>
                          <Circle />
                          {item.holidayName}
                        </HolidayName>
                      ))}
                    </Div>
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : null}
    </DatePickerWrapper>
  );
};
export default DateRangePicker;
